/**
 * Đọc điểm ảnh của một bậc ảnh (≤ 1600px) cho biểu đồ sáng, báo cháy, ống hút, lớp khác biệt.
 *
 * Trình duyệt chỉ cho đọc điểm ảnh của ảnh khác origin khi kho trả tiêu đề CORS — không có thì
 * canvas bị "nhiễm" và `getImageData` ném `SecurityError`. Khi đó trả `blocked` kèm lý do để màn
 * khoá đúng ba công cụ này và nói rõ, phần còn lại của bộ soi vẫn chạy (spec 2026-10-28 §4.3).
 */

import { useEffect, useState } from "react";

export type PixelData = { data: Uint8ClampedArray; w: number; h: number };
export type PixelState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; pixels: PixelData }
  | { status: "blocked"; reason: string };

/** Cạnh dài tối đa khi đọc — đủ cho biểu đồ / ống hút, nhẹ cho máy yếu. */
const MAX_EDGE = 1600;

export const PIXELS_BLOCKED_REASON = "Kho ảnh chưa cho đọc màu điểm ảnh — công cụ này tạm khoá";

export function readPixels(img: HTMLImageElement, maxEdge = MAX_EDGE): PixelData {
  const k = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * k));
  const h = Math.max(1, Math.round(img.naturalHeight * k));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("no-2d");
  ctx.drawImage(img, 0, 0, w, h);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h };
}

/** `url` rỗng hoặc `enabled` false ⇒ không tải gì (công cụ chưa bật thì không tốn mạng). */
export function usePixelReader(url: string | undefined, enabled: boolean): PixelState {
  const [state, setState] = useState<{ key: string; value: PixelState }>({ key: "", value: { status: "idle" } });
  const key = enabled && url ? url : "";

  useEffect(() => {
    if (!key) return;
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    const done = (value: PixelState) => {
      if (alive) setState({ key, value });
    };
    img.onload = () => {
      try {
        done({ status: "ready", pixels: readPixels(img) });
      } catch {
        done({ status: "blocked", reason: PIXELS_BLOCKED_REASON });
      }
    };
    img.onerror = () => done({ status: "blocked", reason: PIXELS_BLOCKED_REASON });
    img.src = key;
    return () => {
      alive = false;
      img.onload = null;
      img.onerror = null;
    };
  }, [key]);

  if (!key) return { status: "idle" };
  return state.key === key ? state.value : { status: "loading" };
}
